import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    Image,
    View
} from "react-native";
import type CompanyRequestsProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitTheme,
    TextInput,
    Sticker,
    Button,
    Dialog,
    Text
} from "ncore-ui-kit";
import type {
    CompanyUpdateRequestHistoryItemType
} from "../../../../services/restAPI/actions/getAllCompanyUpdateRequestHistories/type";
import type {
    CompanyRequestItemType
} from "../../../../services/restAPI/actions/getAllCompanyUpdateRequests/type";
import {
    Building2Icon,
    SearchIcon,
    CheckIcon,
    UserIcon,
    EyeIcon,
    XIcon
} from "lucide-react-native";
import moment from "moment";
import RESTServiceContext from "../../../../services/restAPI/context";
import Pagination from "../../../../components/pagination";
import CompanyDetailModal from "../companyDetailModal";
import RejectReasonModal from "../rejectReasonModal";

const CompanyRequests = ({
    refreshKey = 0
}: CompanyRequestsProps) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        items,
        setItems
    ] = useState<CompanyRequestItemType[]>([]);

    const [
        historyItems,
        setHistoryItems
    ] = useState<CompanyUpdateRequestHistoryItemType[]>([]);

    const [
        tabMode,
        setTabMode
    ] = useState<"pending" | "history">("pending");

    const [
        historyReasonFilter,
        setHistoryReasonFilter
    ] = useState<"" | "approved" | "rejected">("");

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        actionLoading,
        setActionLoading
    ] = useState<boolean>(false);

    const [
        totalCount,
        setTotalCount
    ] = useState<number>(0);

    const [
        historyTotalCount,
        setHistoryTotalCount
    ] = useState<number>(0);

    const [
        page,
        setPage
    ] = useState<number>(1);

    const [
        historyPage,
        setHistoryPage
    ] = useState<number>(1);

    const [
        search,
        setSearch
    ] = useState<string>("");

    const [
        selectedHistoryItem,
        setSelectedHistoryItem
    ] = useState<CompanyUpdateRequestHistoryItemType | null>(null);

    const [
        selectedRequestID,
        setSelectedRequestID
    ] = useState<string | null>(null);

    const [
        isHistoryDetail,
        setIsHistoryDetail
    ] = useState<boolean>(false);

    const [
        isDetailVisible,
        setIsDetailVisible
    ] = useState<boolean>(false);

    const [
        isApproveDialogVisible,
        setIsApproveDialogVisible
    ] = useState<boolean>(false);

    const [
        approveTargetID,
        setApproveTargetID
    ] = useState<string | null>(null);

    const [
        isRejectModalVisible,
        setIsRejectModalVisible
    ] = useState<boolean>(false);

    const [
        rejectTargetID,
        setRejectTargetID
    ] = useState<string | null>(null);

    const fetchPendingRequests = useCallback(async () => {
        try {
            setLoading(true);
            const response = await RESTServiceContext.action("getAllCompanyUpdateRequests", {
                search: search.trim() || undefined,
                page
            });

            if (response.payload) {
                const payloadItems = Array.isArray(response.payload) ? response.payload : [];
                setItems(payloadItems);
                setTotalCount(response.totalCount || payloadItems.length || 0);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [
        search,
        page
    ]);

    const fetchHistoryRequests = useCallback(async () => {
        try {
            setLoading(true);
            const response = await RESTServiceContext.action("getAllCompanyUpdateRequestHistories", {
                reason: historyReasonFilter || undefined,
                search: search.trim() || undefined,
                page: historyPage
            });

            if (response.payload) {
                const payloadItems = Array.isArray(response.payload) ? response.payload : [];
                setHistoryItems(payloadItems);
                setHistoryTotalCount(response.totalCount || payloadItems.length || 0);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [
        historyReasonFilter,
        historyPage,
        search
    ]);

    const reloadData = useCallback(() => {
        if (tabMode === "pending") {
            fetchPendingRequests();
        } else {
            fetchHistoryRequests();
        }
    }, [
        fetchPendingRequests,
        fetchHistoryRequests,
        tabMode
    ]);

    useEffect(() => {
        if (tabMode === "pending") {
            fetchPendingRequests();
        } else {
            fetchHistoryRequests();
        }
    }, [
        fetchPendingRequests,
        fetchHistoryRequests,
        refreshKey,
        tabMode
    ]);

    const handleOpenApprove = (requestID: string) => {
        setApproveTargetID(requestID);
        setIsApproveDialogVisible(true);
    };

    const handleConfirmApprove = async () => {
        if (!approveTargetID) {
            return;
        }

        try {
            setActionLoading(true);
            const response = await RESTServiceContext.action("approveCompanyUpdateRequest", {
                requestID: approveTargetID
            });

            if (!response.isError) {
                NCoreUIKitSnackBar.open({
                    title: localize("requests-action-success")
                });
                setIsApproveDialogVisible(false);
                setApproveTargetID(null);
                setIsDetailVisible(false);
                reloadData();
            } else {
                NCoreUIKitSnackBar.open({
                    title: response.message || localize("requests-action-error")
                });
            }
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : localize("requests-action-error");
            NCoreUIKitSnackBar.open({
                title: errorMessage
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleOpenReject = (requestID: string) => {
        setRejectTargetID(requestID);
        setIsRejectModalVisible(true);
    };

    const handleConfirmReject = async (reason: string) => {
        if (!rejectTargetID) {
            return;
        }

        try {
            setActionLoading(true);
            const response = await RESTServiceContext.action("rejectCompanyUpdateRequest", {
                rejectReason: reason.trim() || undefined,
                requestID: rejectTargetID
            });

            if (!response.isError) {
                NCoreUIKitSnackBar.open({
                    title: localize("requests-action-success")
                });
                setIsRejectModalVisible(false);
                setIsDetailVisible(false);
                setRejectTargetID(null);
                reloadData();
            } else {
                NCoreUIKitSnackBar.open({
                    title: response.message || localize("requests-action-error")
                });
            }
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : localize("requests-action-error");
            NCoreUIKitSnackBar.open({
                title: errorMessage
            });
        } finally {
            setActionLoading(false);
        }
    };

    const renderPendingCard = (item: CompanyRequestItemType) => {
        const isNew = item.requestType === "new";

        return <View
            style={[
                stylesheet.cardContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.content.border.subtle,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.lg,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingLg
                }
            ]}
            key={item.requestID}
        >
            <View
                style={[
                    stylesheet.cardInfoContainer,
                    {
                        gap: spaces.spacingMd,
                        flex: 1
                    }
                ]}
            >
                {
                    item.logoPath ?
                        <Image
                            style={[
                                stylesheet.companyLogo,
                                {
                                    borderRadius: radiuses.md
                                }
                            ]}
                            source={{
                                uri: item.logoPath
                            }}
                        />
                    :
                        <View
                            style={[
                                stylesheet.companyLogo,
                                {
                                    backgroundColor: colors.content.container.subtle,
                                    borderRadius: radiuses.md,
                                    justifyContent: "center",
                                    alignItems: "center"
                                }
                            ]}
                        >
                            <Building2Icon
                                color={colors.content.icon.mid}
                                size={24}
                            />
                        </View>
                }
                <View
                    style={[
                        stylesheet.cardDetailsContainer,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.cardTitleContainer,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            customColor={colors.content.text.high}
                            style={stylesheet.companyTitleText}
                            variant="titleSmallSize"
                        >
                            {item.title}
                        </Text>
                        <Sticker
                            title={isNew ? localize("requests-type-new-company") : localize("requests-type-edit-company")}
                            type={isNew ? "warning" : "success"}
                            spreadBehaviour="free"
                            size="small"
                        />
                    </View>
                    <Text
                        customColor={colors.content.text.low}
                        variant="labelSmallSize"
                    >
                        {moment(item.createdAt).format("DD.MM.YYYY HH:mm")}
                    </Text>
                    {
                        item.userName ?
                            <View
                                style={[
                                    stylesheet.cardSubmitterContainer,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <UserIcon
                                    color={colors.content.icon.mid}
                                    size={14}
                                />
                                <Text
                                    customColor={colors.content.text.mid}
                                    variant="bodySmallSize"
                                >
                                    {localize("requests-submitted-by")}: {item.userName} {item.positionName ? `(${item.positionName})` : ""}
                                </Text>
                            </View>
                        :
                            null
                    }
                </View>
            </View>
            <View
                style={[
                    stylesheet.cardActionsContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    title={localize("requests-action-inspect")}
                    customTextColor={colors.project.brandWhite}
                    onPress={() => {
                        setSelectedHistoryItem(null);
                        setSelectedRequestID(item.requestID);
                        setIsHistoryDetail(false);
                        setIsDetailVisible(true);
                    }}
                    icon={({
                        size
                    }) => <EyeIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                    size="medium"
                />
                <Button
                    onPress={() => handleOpenReject(item.requestID)}
                    title={localize("requests-action-reject")}
                    icon={({
                        color,
                        size
                    }) => <XIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    spreadBehaviour="free"
                    variant="filled"
                    size="medium"
                    type="danger"
                />
                <Button
                    onPress={() => handleOpenApprove(item.requestID)}
                    title={localize("requests-action-approve")}
                    icon={({
                        color,
                        size
                    }) => <CheckIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    spreadBehaviour="free"
                    variant="filled"
                    type="success"
                    size="medium"
                />
            </View>
        </View>;
    };

    const renderHistoryCard = (item: CompanyUpdateRequestHistoryItemType) => {
        const isNew = item.requestType === "new";
        const isApproved = item.reason === "approved" || item.reason === "accepted" || item.reason === "completed";

        return <View
            style={[
                stylesheet.cardContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.content.border.subtle,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.lg,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingLg
                }
            ]}
            key={item.historyID}
        >
            <View
                style={[
                    stylesheet.cardInfoContainer,
                    {
                        gap: spaces.spacingMd,
                        flex: 1
                    }
                ]}
            >
                {
                    item.logoPath ?
                        <Image
                            style={[
                                stylesheet.companyLogo,
                                {
                                    borderRadius: radiuses.md
                                }
                            ]}
                            source={{
                                uri: item.logoPath
                            }}
                        />
                    :
                        <View
                            style={[
                                stylesheet.companyLogo,
                                {
                                    backgroundColor: colors.content.container.subtle,
                                    borderRadius: radiuses.md,
                                    justifyContent: "center",
                                    alignItems: "center"
                                }
                            ]}
                        >
                            <Building2Icon
                                color={colors.content.icon.mid}
                                size={24}
                            />
                        </View>
                }
                <View
                    style={[
                        stylesheet.cardDetailsContainer,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.cardTitleContainer,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            customColor={colors.content.text.high}
                            style={stylesheet.companyTitleText}
                            variant="titleSmallSize"
                        >
                            {item.title}
                        </Text>
                        <Sticker
                            title={isNew ? localize("requests-type-new-company") : localize("requests-type-edit-company")}
                            type={isNew ? "warning" : "success"}
                            spreadBehaviour="free"
                            size="small"
                        />
                    </View>
                    <Text
                        customColor={colors.content.text.low}
                        variant="labelSmallSize"
                    >
                        {`${localize("requests-processed-date")}: ${moment(item.updatedAt || item.createdAt).format("DD.MM.YYYY HH:mm")}`}
                    </Text>
                    {
                        item.userName ?
                            <View
                                style={[
                                    stylesheet.cardSubmitterContainer,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <UserIcon
                                    color={colors.content.icon.mid}
                                    size={14}
                                />
                                <Text
                                    customColor={colors.content.text.mid}
                                    variant="bodySmallSize"
                                >
                                    {localize("requests-submitted-by")}: {item.userName} {item.positionName ? `(${item.positionName})` : ""}
                                </Text>
                            </View>
                        :
                            null
                    }
                    {
                        !isApproved && item.reason && item.reason !== "rejected" ?
                            <Text
                                customColor={colors.content.text.low}
                                variant="labelSmallSize"
                            >
                                {`${localize("requests-rejection-reason")}: ${item.reason === "canceledByUser" ? localize("requests-reason-canceledByUser") : item.reason === "editedByUser" ? localize("requests-reason-editedByUser") : item.reason}`}
                            </Text>
                        :
                            null
                    }
                </View>
            </View>
            <View
                style={[
                    stylesheet.cardActionsContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Sticker
                    title={isApproved ? localize("requests-status-approved") : localize("requests-status-rejected")}
                    type={isApproved ? "success" : "danger"}
                    spreadBehaviour="free"
                    size="small"
                />
                <Button
                    onPress={() => {
                        setSelectedHistoryItem(item);
                        setSelectedRequestID(item.requestID || item.historyID);
                        setIsHistoryDetail(true);
                        setIsDetailVisible(true);
                    }}
                    title={localize("requests-action-inspect")}
                    customTextColor={colors.project.brandWhite}
                    icon={({
                        size
                    }) => <EyeIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                    size="medium"
                />
            </View>
        </View>;
    };

    const renderListContent = () => {
        if (loading) {
            return <View
                style={stylesheet.loadingContainer}
            >
                <ActivityIndicator
                    color={colors.content.text.high}
                    size="large"
                />
            </View>;
        }

        const isPending = tabMode === "pending";
        const currentItems = isPending ? items : historyItems;
        const currentCount = isPending ? totalCount : historyTotalCount;
        const currentPage = isPending ? page : historyPage;

        if (currentItems.length === 0) {
            return <View
                style={stylesheet.emptyContainer}
            >
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {isPending ? localize("requests-empty-list") : localize("requests-history-empty")}
                </Text>
            </View>;
        }

        return <View
            style={[
                stylesheet.listContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            {
                isPending ?
                    items.map(renderPendingCard)
                    :
                    historyItems.map(renderHistoryCard)
            }
            {
                currentCount > 20 ?
                    <View
                        style={stylesheet.paginationContainer}
                    >
                        <Pagination
                            onSelect={(paginationItem) => {
                                if (isPending) {
                                    setPage(paginationItem.pageNumber);
                                } else {
                                    setHistoryPage(paginationItem.pageNumber);
                                }
                            }}
                            totalDataCount={currentCount}
                            currentPage={currentPage}
                            itemPerPage={20}
                        />
                    </View>
                :
                    null
            }
        </View>;
    };

    return <View
        style={[
            stylesheet.container,
            {
                gap: spaces.spacingLg
            }
        ]}
    >
        <View
            style={[
                stylesheet.toolbarContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={stylesheet.searchInputContainer}
            >
                <TextInput
                    placeholder={localize("requests-search-placeholder")}
                    customColor={colors.project.brandWhite}
                    icon={({
                        size
                    }) => <SearchIcon
                        color={colors.content.icon.mid}
                        size={size}
                    />}
                    onChangeText={(val) => {
                        setSearch(val);
                        if (tabMode === "pending") {
                            setPage(1);
                        } else {
                            setHistoryPage(1);
                        }
                    }}
                    key="company-requests-search"
                    spreadBehaviour="stretch"
                    value={search}
                />
            </View>
            <View
                style={[
                    stylesheet.toolbarActionsContainer,
                    {
                        backgroundColor: colors.content.container.subtle,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXs,
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Button
                    customTextColor={tabMode === "pending" ? colors.project.brandWhite : colors.content.text.mid}
                    customColor={tabMode === "pending" ? "gold" : undefined}
                    variant={tabMode === "pending" ? "filled" : "ghost"}
                    title={localize("requests-filter-pending")}
                    onPress={() => {
                        setTabMode("pending");
                    }}
                    spreadBehaviour="free"
                    type="neutral"
                />
                <Button
                    customTextColor={tabMode === "history" ? colors.project.brandWhite : colors.content.text.mid}
                    customColor={tabMode === "history" ? "gold" : undefined}
                    variant={tabMode === "history" ? "filled" : "ghost"}
                    title={localize("requests-filter-history")}
                    onPress={() => {
                        setTabMode("history");
                    }}
                    spreadBehaviour="free"
                    type="neutral"
                />
            </View>
        </View>
        {
            tabMode === "history" ?
                <View
                    style={[
                        stylesheet.historyFilterContainer,
                        {
                            backgroundColor: colors.content.container.subtle,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingXs,
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <Button
                        customTextColor={historyReasonFilter === "" ? colors.project.brandWhite : colors.content.text.mid}
                        customColor={historyReasonFilter === "" ? "gold" : undefined}
                        variant={historyReasonFilter === "" ? "filled" : "ghost"}
                        title={localize("requests-filter-all")}
                        onPress={() => {
                            setHistoryReasonFilter("");
                            setHistoryPage(1);
                        }}
                        spreadBehaviour="free"
                        type="neutral"
                        size="small"
                    />
                    <Button
                        customTextColor={historyReasonFilter === "approved" ? colors.project.brandWhite : colors.content.text.mid}
                        customColor={historyReasonFilter === "approved" ? "gold" : undefined}
                        variant={historyReasonFilter === "approved" ? "filled" : "ghost"}
                        title={localize("requests-filter-approved")}
                        onPress={() => {
                            setHistoryReasonFilter("approved");
                            setHistoryPage(1);
                        }}
                        spreadBehaviour="free"
                        type="neutral"
                        size="small"
                    />
                    <Button
                        customTextColor={historyReasonFilter === "rejected" ? colors.project.brandWhite : colors.content.text.mid}
                        customColor={historyReasonFilter === "rejected" ? "gold" : undefined}
                        variant={historyReasonFilter === "rejected" ? "filled" : "ghost"}
                        title={localize("requests-filter-rejected")}
                        onPress={() => {
                            setHistoryReasonFilter("rejected");
                            setHistoryPage(1);
                        }}
                        spreadBehaviour="free"
                        type="neutral"
                        size="small"
                    />
                </View>
            :
                null
        }
        {renderListContent()}
        <CompanyDetailModal
            historyItem={isHistoryDetail ? selectedHistoryItem : null}
            onApprove={(id) => handleOpenApprove(id)}
            onClose={() => {
                setSelectedHistoryItem(null);
                setSelectedRequestID(null);
                setIsDetailVisible(false);
            }}
            requestID={selectedRequestID}
            isHistory={isHistoryDetail}
            isVisible={isDetailVisible}
            onReject={(id) => {
                handleOpenReject(id);
            }}
        />
        <RejectReasonModal
            onClose={() => {
                setRejectTargetID(null);
                setIsRejectModalVisible(false);
            }}
            isVisible={isRejectModalVisible}
            onConfirm={handleConfirmReject}
            isLoading={actionLoading}
        />
        <Dialog
            primaryButtonProps={{
                title: localize("requests-approve-confirm-title"),
                onPress: handleConfirmApprove
            }}
            content={localize("requests-approve-confirm-desc")}
            secondaryButtonProps={{
                onPress: () => {
                    setIsApproveDialogVisible(false);
                    setApproveTargetID(null);
                },
                title: localize("cancel")
            }}
            onOverlayPress={() => {
                setIsApproveDialogVisible(false);
                setApproveTargetID(null);
            }}
            onClosed={() => {
                setIsApproveDialogVisible(false);
                setApproveTargetID(null);
            }}
            isVisible={isApproveDialogVisible}
            variant="yes-no"
        />
    </View>;
};
export default CompanyRequests;
